import { useMemo } from 'react';
import type { TimeSeriesResponse } from 'api';
import { currencyFormatZeroToTwoDecimals } from 'common/formats/formats';
import { useStore } from 'common/store';
import { ChartSeries } from 'components/Charts';
import { DEFAULT_CURRENCY } from 'components/FeaturedSpreadsheet/constants';
import { CompanyMetricName, useGetCompanyMetric } from 'services/hooks/companies';
import { formatNumbers } from 'utilities';
import { UseChartDataParams, UseChartDataReturn } from '../types';
import { buildMetricLineSeriesConfig } from './buildMetricLineSeriesConfig';

const USE_MOCK_DATA = false;

export interface UseCompanyMetricChartParams extends UseChartDataParams {
  metricName: CompanyMetricName;
  label: string;
  color: string;
  emphasisColor?: string;
  mockData?: TimeSeriesResponse | null;
}

export function useCompanyMetricChart({
  selectedMeasurementDate,
  units,
  metricName,
  label,
  color,
  emphasisColor,
  mockData,
}: UseCompanyMetricChartParams): UseChartDataReturn {
  const [{ companyInfo }] = useStore();

  const {
    data: apiData,
    isLoading: isLoadingMetric,
    isError: isErrorMetric,
  } = useGetCompanyMetric<TimeSeriesResponse>({
    company: companyInfo,
    metricName,
    measurementDate: selectedMeasurementDate,
    shouldQueryAutomatically: !USE_MOCK_DATA,
  });

  const metricData = USE_MOCK_DATA && mockData ? mockData : apiData;

  const lineChartSeries: ChartSeries[] = useMemo(() => {
    if (!metricData?.history) {
      return [];
    }

    const dataPoints = metricData.history.map(item => ({
      x: item.date,
      y: Number(item.value),
    }));

    const config = buildMetricLineSeriesConfig(color, emphasisColor);

    return [
      {
        id: metricName,
        label,
        data: dataPoints,
        color,
        config,
      },
    ];
  }, [metricData, metricName, label, color, emphasisColor]);

  const indicatorValue = useMemo(() => {
    if (!metricData) {
      return { value: '0', percentage: 0 };
    }

    const formattedValue = formatNumbers({
      value: Number(metricData.current.value),
      format: currencyFormatZeroToTwoDecimals,
      prefix: '',
      suffix: '',
      allowNegativeValue: true,
      currency: {
        code: DEFAULT_CURRENCY,
        units,
      },
      currencyCode: DEFAULT_CURRENCY,
      blankIfNull: false,
      staticCurrency: undefined as unknown as { code: string; units: string },
    });

    return {
      value: formattedValue,
      percentage: Number(metricData.change_percent),
    };
  }, [metricData, units]);

  return {
    lineChartSeries,
    indicatorValue,
    isLoading: USE_MOCK_DATA && mockData ? false : isLoadingMetric,
    isError: USE_MOCK_DATA && mockData ? false : isErrorMetric,
  };
}
